import React, {useEffect, useState} from 'react'
import {remCalc} from '../../theme/_utils'
import {
    Drawer,
    DrawerBody,
    DrawerContent,
    Box,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {motion, AnimatePresence} from 'framer-motion'

import DrawerMenuFirstLevel from './drawer-menu-first-level'
import DrawerMenuSecondLevel from './drawer-menu-second-level'
import {useGetMainMenu} from '../../custom-api/hooks/use-get-main-menu'
import {useLocation} from 'react-router-dom'

const DrawerMenu = ({isOpen, onClose = noop}) => {
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [ariaBusy, setAriaBusy] = useState('true')
    const [drawerMenuTopMargin, setDrawerMenuTopMargin] = useState(0)
    const location = useLocation()

    useEffect(() => {
        setAriaBusy('false')
        if (typeof document === 'undefined') return

        const header = document.getElementById('main-header')
        const sovraHeader = document.getElementById('sovra-header')

        function updateHeaderHeight() {
            let sovraHeaderHeight = 0

            if (sovraHeader) {
                const rect = sovraHeader.getBoundingClientRect()
                if (rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight) {
                    sovraHeaderHeight = rect.height
                }
            }

            const headerHeight = header ? header.offsetHeight : 0
            const total = headerHeight + sovraHeaderHeight
            setDrawerMenuTopMargin(total)
            document.documentElement.style.setProperty('--drawer-menu-top', `${total}px`)
        }

        updateHeaderHeight()

        window.addEventListener('scroll', updateHeaderHeight)
        window.addEventListener('resize', updateHeaderHeight)

        return () => {
            window.removeEventListener('scroll', updateHeaderHeight)
            window.removeEventListener('resize', updateHeaderHeight)
        }
    }, [])

    const {data} = useGetMainMenu('braun_')

    const handleCategorySelection = (categoryUid) => {
        const foundCategory = data.primarycategories.find((cat) => cat.uid === categoryUid)
        setSelectedCategory(foundCategory || null)
    }

    const onDrawerClose = () => {
        setSelectedCategory(null)
        onClose()
    }

    useEffect(() => {
        onDrawerClose()
    }, [location])

    // Variants for animations
    const secondLevelVariants = {
        initial: {x: '100%'}, // start offscreen right
        enter: {x: 0, transition: {duration: 0.3}},
        exit: {x: '100%', transition: {duration: 0.3}}, // exit to right
    }

    const firstLevelVariants = {
        initial: {x: 0}, // always in place
        enter: {x: 0, transition: {duration: 0.3}},
        exit: {x: 0, transition: {duration: 0.3}},
    }

    return (
        <Drawer
            data-testid="braun-drawer"
            isOpen={isOpen}
            onClose={onDrawerClose}
            placement="left"
            size="md"
            blockScrollOnMount={false}
        >
            {/**Black placeholder to hide the white space when sovra header hides and menu heights readjusts */}
            <Box
                position="fixed"
                top={0}
                left={0}
                right={0}
                height={remCalc(200)}
                backgroundColor="black"
                zIndex={1}
            />
            <DrawerContent
                data-testid="braun-drawer-content"
                overflow="scroll"
                w="100vw"
                maxW="100vw"
                mt={`var(--drawer-menu-top, ${drawerMenuTopMargin}px)`}
                sx={{
                    height: '100vh',
                    maxHeight: '100vh',
                    '@supports (height: 100dvh)': {
                        height: '100dvh',
                        maxHeight: '100dvh',
                    },
                }}
                zIndex="2"
            >
                <DrawerBody
                    data-testid="braun-drawer-body"
                    background="background.dark"
                    sx={{
                        px: remCalc(16),
                        pt: {base: remCalc(24), md: remCalc(40)},
                        pb: {base: remCalc(84), sm: remCalc(94), md: remCalc(94)},
                        '@supports (height: 100dvh)': {
                            pb: {base: remCalc(16), md: remCalc(40)},
                        },
                    }}
                >
                    <div
                        id="category-nav"
                        aria-live="polite"
                        aria-busy={ariaBusy}
                        aria-atomic="true"
                    >
                        {data && (
                            <>
                                {/* First Level */}
                                <AnimatePresence initial={false} mode="popLayout">
                                    {!selectedCategory && (
                                        <motion.div
                                            key="first-level"
                                            initial="initial"
                                            animate="enter"
                                            exit="exit"
                                            variants={firstLevelVariants}
                                        >
                                            <DrawerMenuFirstLevel
                                                categoriesData={data}
                                                setSelectedCategory={handleCategorySelection}
                                            />
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Second Level */}
                                <AnimatePresence initial={false} mode="popLayout">
                                    {selectedCategory && (
                                        <motion.div
                                            key={selectedCategory.uid}
                                            initial="initial"
                                            animate="enter"
                                            exit="exit"
                                            variants={secondLevelVariants}
                                            style={{
                                                position: 'fixed',
                                                width: '100%',
                                                top: 0,
                                                left: 0,
                                                bottom: 0,
                                                right: 0,
                                                zIndex: 9999,
                                                backgroundColor: 'white',
                                                overflowY: 'auto',
                                            }}
                                        >
                                            <DrawerMenuSecondLevel
                                                selectedCategory={selectedCategory}
                                                setSelectedCategory={setSelectedCategory}
                                                onClose={onClose}
                                            />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </>
                        )}
                    </div>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}

DrawerMenu.displayName = 'DrawerMenu'
DrawerMenu.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onWishlistClick: PropTypes.func,
}

export {DrawerMenu}
